import { useEffect, useMemo, useState } from "react";
import { bulkUpdateSipStatus } from "../../../admin/api";
import { useWorkspaceSelection } from "../workspaceSelection/WorkspaceSelectionContext";
import { createTransferSelectionItem, getWorkspaceSelectionKey, prepareUniqueSipItems } from "../workspaceSelection/workspaceSelectionUtils";
import { useSessionSipActivity } from "../sessionActivity/SessionSipActivityContext";
import { useAdminNotifications } from "../notifications/AdminNotificationsContext";
import { useAdminDataRefresh } from "../dataRefresh/AdminDataRefreshContext";
import AdminStatusBadge from "../shared/AdminStatusBadge";

const styles = {
  overlay: { position: "fixed", inset: 0, zIndex: 6100, display: "flex", alignItems: "center", justifyContent: "center", padding: "var(--admin-modal-gutter)", background: "rgba(5, 10, 18, 0.72)" },
  modal: { width: "min(var(--admin-modal-md), calc(100vw - 2 * var(--admin-modal-gutter)))", maxHeight: "calc(100dvh - 2 * var(--admin-modal-gutter))", overflowY: "auto", boxSizing: "border-box", padding: "var(--admin-modal-padding)", border: "1px solid var(--border)", borderRadius: "var(--admin-modal-radius)", background: "var(--header-bg)", boxShadow: "var(--shadow-panel)" },
  title: { margin: 0, color: "var(--text-main)", fontSize: "var(--admin-heading-size)" },
  summary: { margin: "var(--admin-gap-sm) 0", color: "var(--text-muted)", fontSize: "var(--admin-text-sm)", lineHeight: 1.4 },
  warning: { margin: "var(--admin-gap) 0", padding: "var(--admin-control-padding-y)", borderRadius: "var(--admin-radius)", background: "var(--danger-soft)", color: "var(--danger)", fontSize: "var(--admin-text-sm)" },
  table: { width: "100%", borderCollapse: "collapse", marginTop: "var(--admin-gap-lg)", fontSize: "var(--admin-text-sm)" },
  cell: { padding: "var(--admin-table-cell-y) var(--admin-table-cell-x)", borderBottom: "1px solid var(--border)", color: "var(--text-main)", textAlign: "left" },
  actions: { display: "flex", justifyContent: "flex-end", gap: "var(--admin-gap-sm)", marginTop: "var(--admin-gap-lg)", flexWrap: "wrap" },
  button: { border: "1px solid var(--border-strong)", borderRadius: "var(--admin-radius)", padding: "var(--admin-button-padding-y) var(--admin-button-padding-x)", background: "var(--card-bg-soft)", color: "var(--text-main)", fontWeight: 800, cursor: "pointer" },
  primary: { border: "none", background: "var(--accent)", color: "var(--button-text)" },
  error: { margin: "10px 0", color: "var(--danger)", fontSize: "13px" },
  success: { margin: "10px 0", color: "var(--success)", fontSize: "13px" },
};

export default function BulkSipStatusModal({
  isOpen,
  items,
  status,
  sourceSection = "admin",
  excludedComplexCount = 0,
  onClose,
  onApplied,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const { updateSnapshots } = useWorkspaceSelection();
  const { recordChanges } = useSessionSipActivity();
  const { invalidate } = useAdminDataRefresh();
  const { notify } = useAdminNotifications();
  const sipItems = useMemo(() => prepareUniqueSipItems(items), [items]);
  const uniqueComplexCount = useMemo(
    () => new Set(sipItems.map((item) => item.complexId)).size,
    [sipItems]
  );
  const currentClientCount = useMemo(
    () => sipItems.filter((item) =>
      String(item.snapshot?.currentSipClientId || "") === String(item.sipClientId || "")
    ).length,
    [sipItems]
  );
  const resultByKey = useMemo(
    () => new Map((result?.results || []).map((item) => [item.key, item])),
    [result]
  );

  useEffect(() => {
    if (!isOpen) {
      setError("");
      setResult(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      setError("");
      setResult(null);
      const response = await bulkUpdateSipStatus({
        items: sipItems.map((item) => ({ complexId: item.complexId, sipClientId: item.sipClientId })),
        status,
      }, sourceSection);
      const successful = response.results.filter((item) => item.ok);
      const rootPatches = new Map(
        successful
          .filter((item) => item.complexPatch)
          .map((item) => [String(item.complexId), item.complexPatch])
      );
      const snapshotUpdatesByKey = new Map();

      successful.forEach((item) => {
        snapshotUpdatesByKey.set(item.key, {
          clientStatus: item.status,
          ...(item.complexPatch
            ? {
                complexStatus: item.complexPatch.status,
                currentSipClientId: item.complexPatch.currentSipClientId || "",
              }
            : {}),
        });
        const selected = sipItems.find((sipItem) => getWorkspaceSelectionKey(sipItem) === item.key);
        const transferKey = getWorkspaceSelectionKey(createTransferSelectionItem({
          complexId: item.complexId,
          sipClientId: item.sipClientId,
          clientName: item.clientName,
          transfer_number: selected?.snapshot?.transferNumber,
        }));
        if (transferKey) snapshotUpdatesByKey.set(transferKey, { clientStatus: item.status });
      });

      sipItems.forEach((item) => {
        const patch = rootPatches.get(String(item.complexId));
        if (!patch) return;
        const key = getWorkspaceSelectionKey(item);
        snapshotUpdatesByKey.set(key, {
          ...(snapshotUpdatesByKey.get(key) || {}),
          complexStatus: patch.status,
          currentSipClientId: patch.currentSipClientId || "",
        });
      });

      rootPatches.forEach((patch, complexId) => {
        snapshotUpdatesByKey.set(getWorkspaceSelectionKey({ entityType: "complex", complexId }), {
          rootStatus: patch.status,
          currentSipClientId: patch.currentSipClientId || "",
        });
      });
      updateSnapshots(
        Array.from(snapshotUpdatesByKey, ([key, snapshot]) => ({ key, snapshot }))
      );
      if (successful.length) invalidate(["complexes", "sip", "transfers"]);
      const selectedByKey = new Map(sipItems.map((item) => [getWorkspaceSelectionKey(item), item]));
      recordChanges(
        successful.filter((item) => item.changed).map((item) => {
          const selected = selectedByKey.get(item.key);
          return {
            ...selected,
            changedFields: ["status"],
            snapshot: {
              ...selected?.snapshot,
              clientStatus: item.status,
              ...(item.complexPatch ? { complexStatus: item.complexPatch.status, currentSipClientId: "" } : {}),
            },
          };
        }),
        "bulk-status"
      );
      onApplied?.(response.results);
      setResult(response);
      notify(`Статус клиентов: ${response.updatedCount} изменено, ${response.unchangedCount} без изменений, ${response.failedCount} ошибок`, response.failedCount ? "partial" : "success");
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : "Не удалось изменить статус";
      setError(message); notify(message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const actionLabel = status ? "Включение клиентов" : "Остановка клиентов";

  return (
    <div style={styles.overlay} role="presentation">
      <div role="dialog" aria-modal="true" aria-label={actionLabel} style={styles.modal}>
        <h2 style={styles.title}>{actionLabel}</h2>
        <p style={styles.summary}>
          Клиентов: {sipItems.length} · уникальных ЖК: {uniqueComplexCount}<br />
          ЖК без выбранного клиента: {excludedComplexCount} — не участвуют
        </p>
        {!status && currentClientCount > 0 && (
          <div style={styles.warning}>
            Текущих клиентов на карте: {currentClientCount}. Для них будет применена существующая логика остановки ЖК.
          </div>
        )}
        {error && <p style={styles.error}>{error}</p>}
        {result && (
          <p style={result.failedCount > 0 ? styles.error : styles.success}>
            Изменено: {result.updatedCount}. Без изменений: {result.unchangedCount}. Ошибок: {result.failedCount}.
          </p>
        )}
        <table style={styles.table}>
          <thead><tr><th style={styles.cell}>ЖК</th><th style={styles.cell}>Клиент</th><th style={styles.cell}>Текущий статус</th><th style={styles.cell}>Новый статус</th><th style={styles.cell}>На карте</th><th style={styles.cell}>Результат</th></tr></thead>
          <tbody>
            {sipItems.map((item) => {
              const key = getWorkspaceSelectionKey(item);
              const rowResult = resultByKey.get(key);
              const isCurrent = String(item.snapshot?.currentSipClientId || "") === String(item.sipClientId || "");
              return (
                <tr key={key}>
                  <td style={styles.cell}>{item.snapshot?.complexName || "—"}</td>
                  <td style={styles.cell}>{item.clientName || "—"}</td>
                  <td style={styles.cell}>
                    {typeof item.snapshot?.clientStatus === "boolean"
                      ? <AdminStatusBadge active={item.snapshot.clientStatus} />
                      : "Неизвестно"}
                  </td>
                  <td style={styles.cell}><AdminStatusBadge active={status} /></td>
                  <td style={styles.cell}>{isCurrent ? (!status ? "Текущий клиент — будет применена существующая логика остановки ЖК" : "Да") : "Нет"}</td>
                  <td style={styles.cell}>{rowResult ? (rowResult.ok ? (rowResult.changed ? "Изменено" : "Без изменений") : rowResult.error) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={styles.actions}>
          <button type="button" style={styles.button} disabled={isSaving} onClick={onClose}>{result ? "Закрыть" : "Отмена"}</button>
          <button type="button" style={{ ...styles.button, ...styles.primary }} disabled={isSaving || sipItems.length === 0} onClick={handleSubmit}>
            {isSaving ? "Сохраняется..." : "Подтвердить"}
          </button>
        </div>
      </div>
    </div>
  );
}
